import { usePageMeta } from "@/hooks/usePageMeta";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

function AnimatedSection({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  const { ref, isVisible } = useScrollAnimation(0.1);
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${className}`}
      style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? "translateY(0)" : "translateY(30px)" }}
    >
      {children}
    </div>
  );
}

export default function Accessibility() {
  usePageMeta({
    title: "הצהרת נגישות | upsite",
    description: "הצהרת הנגישות של אפסייט – מחויבותנו להנגשת האתר לכלל האוכלוסייה בהתאם לתקן הישראלי.",
  });

  return (
    <div>
      {/* Hero */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-20 overflow-hidden">
        <div className="absolute inset-0 gradient-mesh-strong" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-[#66CDAA]/10 rounded-full blur-[120px]" />
        <div className="container relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 font-[Rubik] leading-tight">
              הצהרת <span className="gradient-text">נגישות</span>
            </h1>
            <p className="text-lg text-white/60 leading-relaxed">
              עודכן לאחרונה: מרץ 2026
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 lg:py-24 section-navy">
        <div className="container">
          <AnimatedSection>
            <div className="max-w-3xl mx-auto space-y-10">

              <div>
                <h2 className="text-2xl font-bold text-white mb-4 font-[Rubik]">מחויבותנו לנגישות</h2>
                <p className="text-white/60 leading-relaxed">
                  אפסייט בע״מ (ח.פ. 513651406) מחויבת להנגשת האתר לכלל האוכלוסייה, לרבות אנשים עם מוגבלויות, וזאת בהתאם לחוק שוויון זכויות לאנשים עם מוגבלות, תשנ"ח-1998, ותקנות שוויון זכויות לאנשים עם מוגבלות (התאמות נגישות לשירות), תשע"ג-2013.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-white mb-4 font-[Rubik]">תקן הנגישות</h2>
                <p className="text-white/60 leading-relaxed">
                  האתר עומד בדרישות תקן ישראלי ת"י 5568 המבוסס על הנחיות WCAG 2.1 ברמת AA. אנו פועלים באופן שוטף לשפר את נגישות האתר ולוודא שכל התכנים והשירותים המוצעים בו נגישים לכלל המשתמשים.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-white mb-4 font-[Rubik]">התאמות הנגישות שבוצעו באתר</h2>
                <ul className="list-disc list-inside space-y-2 text-white/60 leading-relaxed">
                  <li>התאמה לניווט באמצעות מקלדת בלבד</li>
                  <li>קישור "דלג לתוכן" לניווט מהיר</li>
                  <li>שימוש בתגי ARIA ובמבנה HTML סמנטי</li>
                  <li>תגיות alt לתמונות</li>
                  <li>ניגודיות צבעים ברמה נאותה</li>
                  <li>התאמה לקוראי מסך</li>
                  <li>אתר רספונסיבי המותאם לכל סוגי המכשירים</li>
                  <li>תפריט נגיש עם תמיכה בניווט מקלדת</li>
                  <li>תוסף נגישות צד שלישי להתאמה אישית של תצוגת האתר</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-white mb-4 font-[Rubik]">דפדפנים וטכנולוגיה מסייעת</h2>
                <p className="text-white/60 leading-relaxed">
                  האתר תוכנן לתמוך בגרסאות העדכניות של הדפדפנים הנפוצים: Google Chrome, Mozilla Firefox, Apple Safari ו-Microsoft Edge. מומלץ להשתמש בגרסה העדכנית ביותר של הדפדפן לחוויה מיטבית.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-white mb-4 font-[Rubik]">פרטים שעשויים שלא להיות נגישים</h2>
                <p className="text-white/60 leading-relaxed">
                  למרות מאמצינו להנגיש את כל הדפים והתכנים באתר, ייתכן שחלקים מסוימים אינם נגישים באופן מלא. אנו ממשיכים לעבוד על שיפור הנגישות באופן שוטף ומתחייבים לטפל בכל בעיה שתובא לידיעתנו.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-white mb-4 font-[Rubik]">רכז נגישות</h2>
                <p className="text-white/60 leading-relaxed">
                  אם נתקלתם בבעיית נגישות באתר, או אם יש לכם הצעות לשיפור, נשמח לשמוע מכם. ניתן לפנות לרכז הנגישות שלנו:
                </p>
                <ul className="list-none space-y-2 text-white/60 leading-relaxed mt-3">
                  <li>אפסייט בע״מ – Upsite LTD</li>
                  <li>דוא"ל: info@upsite.co.il</li>
                  <li>טלפון: 054-295-0091</li>
                </ul>
                <p className="text-white/60 leading-relaxed mt-3">
                  אנו מתחייבים לטפל בכל פנייה בנושא נגישות בתוך זמן סביר ולעשות מאמץ לספק פתרון הולם.
                </p>
              </div>

            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
